import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("traveler");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await register({ name, email, password, role });
      navigate(u.role === "host" ? "/host" : "/traveler");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="overline mb-3">Begin</div>
        <h1 className="font-serif text-4xl leading-tight">Create your <span className="italic text-moss">Retreat Travel</span> account.</h1>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label className="text-xs mb-1.5 block">I am joining as</Label>
            <RadioGroup value={role} onValueChange={setRole} className="flex gap-4">
              <label data-testid="role-traveler" className={`flex-1 cursor-pointer rounded-xl border p-4 ${role === "traveler" ? "border-moss bg-moss/5" : "border-subtle bg-white"}`}>
                <RadioGroupItem value="traveler" className="sr-only" />
                <div className="font-serif text-xl">Traveler</div>
                <div className="text-xs text-muted2 mt-1">To discover & book journeys</div>
              </label>
              <label data-testid="role-host" className={`flex-1 cursor-pointer rounded-xl border p-4 ${role === "host" ? "border-moss bg-moss/5" : "border-subtle bg-white"}`}>
                <RadioGroupItem value="host" className="sr-only" />
                <div className="font-serif text-xl">Host</div>
                <div className="text-xs text-muted2 mt-1">To list a space or retreat</div>
              </label>
            </RadioGroup>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Full name</Label>
            <Input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} required className="rounded-xl bg-white border-subtle" />
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Email</Label>
            <Input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-xl bg-white border-subtle" />
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Password (min 6 chars)</Label>
            <Input data-testid="register-password" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-xl bg-white border-subtle" />
          </div>
          {err && <div data-testid="register-error" className="text-sm text-destructive">{err}</div>}
          <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full rounded-full bg-moss hover:bg-moss-hover text-canvas py-6">
            {loading ? "Creating…" : "Create account"}
          </Button>
        </form>
        <p className="text-sm text-muted2 mt-8 text-center">
          Already have an account? <Link data-testid="link-login" to="/login" className="text-moss underline underline-offset-4">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
